/* Photo uploads feed the shared cake configurator. Only readiness GETs retry automatically. */
(()=>{
 'use strict';
 const abortError=()=>new DOMException('The upload was cancelled.','AbortError');
 function wait(ms,signal){
  return new Promise((resolve,reject)=>{
   if(signal.aborted){reject(abortError());return;}
   const cancel=()=>{clearTimeout(timer);reject(abortError());};
   const timer=setTimeout(()=>{signal.removeEventListener('abort',cancel);resolve();},ms);
   signal.addEventListener('abort',cancel,{once:true});
  });
 }
 async function fetchJSON(url,options,signal,timeout){
  if(signal.aborted)throw abortError();
  const requestController=new AbortController();let timedOut=false;
  const cancel=()=>requestController.abort();
  signal.addEventListener('abort',cancel,{once:true});
  const timer=setTimeout(()=>{timedOut=true;requestController.abort();},timeout);
  try{
   const response=await fetch(url,{...options,credentials:'omit',cache:'no-store',signal:requestController.signal});
   let result;
   try{result=await response.json();}
   catch(e){
    if(signal.aborted || timedOut)throw e;
    throw new Error('Photo pricing returned an incomplete response. Please try again.');
   }
   if(!response.ok){
    const error=new Error(typeof result?.error==='string'?result.error:'Photo pricing is temporarily unavailable. Please try again.');
    error.status=response.status;throw error;
   }
   return result;
  }catch(e){
   if(signal.aborted)throw abortError();
   if(timedOut){const error=new Error('The request took too long. Please try again.');error.code='TIMEOUT';throw error;}
   throw e;
  }finally{clearTimeout(timer);signal.removeEventListener('abort',cancel);}
 }
 async function waitForService(base,signal,onRetry){
  // Give a sleeping host time to start before submitting a photo for paid analysis.
  for(let attempt=0;attempt<5;attempt++){
   try{
    const health=await fetchJSON(base+'/health',{headers:{Accept:'application/json'}},signal,15000);
    if(health && health.ok===true)return;
   }catch(e){if(signal.aborted)throw e;}
   if(attempt<4){onRetry();await wait(Math.min(1000*2**attempt,4000),signal);}
  }
  throw new Error('We couldn’t connect to photo pricing. Check your connection and try again, or request a quote.');
 }
 function init(root){
  const data=JSON.parse(root.querySelector('[data-cc-config]').textContent);
  if(!data.upload || root.dataset.uploadInitialized)return;
  root.dataset.uploadInitialized='true';
  const q=s=>root.querySelector(s),input=q('[data-photo-input]');
  const review=q('[data-upload-review]'),progress=q('[data-upload-progress]'),remove=q('[data-remove-photo]');
  const retry=q('[data-upload-retry]'),feedback=q('[data-upload-feedback]'),status=q('[data-upload-status]');
  let serial=0,objectUrl=null,controller=null,currentFile=null;
  const dispatch=(name,detail)=>root.dispatchEvent(new CustomEvent(name,{detail}));
  const reset=result=>dispatch('cc:photo-analysis',result);
  function showStatus(message,busy=false){
   if(busy)status.classList.remove('is-detected');
   status.textContent=message;progress.hidden=!busy;
   feedback.setAttribute('aria-busy',String(busy));
  }
  function contact(reference){
   const number=String(data.whatsapp).replace(/\D/g,'');
   review.href='https://wa.me/'+number+'?text='+encodeURIComponent('Hello, I would like a quote for my uploaded cake design.'+(reference?' Reference: '+reference+'.':''));
   review.hidden=false;
  }
  function clearPhoto(){
   ++serial;if(controller)controller.abort();controller=null;currentFile=null;
   reset(null);review.hidden=true;retry.hidden=true;
   showStatus('Upload a photo to see available sizes and prices.');
   const dialog=q('dialog');if(dialog.open)dialog.close();dialog.querySelector('img').removeAttribute('src');
   if(objectUrl){URL.revokeObjectURL(objectUrl);objectUrl=null;}
   const picture=q('[data-main-image]');picture.removeAttribute('src');picture.hidden=true;
   q('[data-upload-empty]').hidden=false;q('[data-zoom]').hidden=true;q('[data-replace-photo]').hidden=true;remove.hidden=true;input.value='';
  }
  async function analyse(file){
   if(input.disabled)return;
   if(controller)controller.abort();
   const request=++serial,activeController=new AbortController();controller=activeController;
   reset(null);review.hidden=true;retry.hidden=true;
   showStatus('Connecting to photo pricing…',true);dispatch('cc:photo-upload-start');
   try{
    let endpoint;
    try{endpoint=new URL(String(data.pricingEndpoint || '').trim());}catch{}
    if(!endpoint || endpoint.protocol!=='https:' || endpoint.username || endpoint.password || endpoint.search || endpoint.hash){
     throw new Error('Photo pricing is being connected. Please request a quote for now.');
    }
    const base=endpoint.href.replace(/\/$/,'');
    const encoded=await new Promise((resolve,reject)=>{
     const reader=new FileReader();
     reader.onload=()=>resolve(String(reader.result).split(',')[1]);
     reader.onerror=()=>reject(new Error('The image could not be read. Please choose another photo.'));
     reader.readAsDataURL(file);
    });
    if(request!==serial)return;
    await waitForService(base,activeController.signal,()=>{
     if(request===serial)showStatus('Photo pricing is taking a little longer to connect. We’re trying again…',true);
    });
    if(request!==serial)return;
    showStatus('Analysing your photo and calculating prices…',true);
    // Do not automatically repeat this POST: a lost response may still have produced a quote.
    const result=await fetchJSON(base+'/estimate',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({image:encoded,taxesIncluded:data.taxesIncluded})},activeController.signal,100000);
    if(request!==serial)return;
    if(!result || !['priced','review'].includes(result.status) || typeof result.message!=='string' || (result.status==='priced' && (!Array.isArray(result.prices) || typeof result.quoteId!=='string'))){
     throw new Error('We couldn’t read the price response. Please try again or request a quote.');
    }
    reset(result);
    const cfg=result && result.config;
    let customerMessage=result.status==='review'?'This design needs review. Please contact us on WhatsApp for final pricing.':'Choose a size to continue.';
    if(cfg && cfg.shaped===true){
     customerMessage='We detected a shaped cake. Please contact us on WhatsApp for final pricing.';
    }else if(cfg && Number.isInteger(cfg.tiers) && cfg.tiers>=1 && cfg.tiers<=4){
     customerMessage='We detected a '+cfg.tiers+'-tier cake.';
     if(result.status==='review')customerMessage+=' Please contact us on WhatsApp for final pricing.';
    }
    status.classList.add('is-detected');
    showStatus(customerMessage);
    if(result.status==='review'){
     contact(result.reference);
     review.textContent='Contact us on WhatsApp →';
    }else{
     review.hidden=true;
    }
   }catch(e){
    if(request!==serial)return;
    status.classList.remove('is-detected');
    const message=e.code==='TIMEOUT'?'Analysis took too long. Your photo is still selected—try again or request a quote.':
     e instanceof TypeError || e.name==='TypeError'?'The connection to photo pricing was interrupted. Your photo is still selected—try again or request a quote.':
     e.message || 'We couldn’t analyse your photo. Please try again or request a quote.';
    showStatus(message);retry.hidden=false;contact();
   }finally{
    if(request===serial){input.value='';progress.hidden=true;feedback.setAttribute('aria-busy','false');controller=null;dispatch('cc:photo-upload-finish');}
   }
  }
  remove.addEventListener('click',()=>{if(input.disabled)return;clearPhoto();input.focus();});
  retry.addEventListener('click',()=>{if(currentFile && !controller && !input.disabled)analyse(currentFile);});
  input.addEventListener('change',()=>{
   const file=input.files[0];if(!file || input.disabled)return;
   clearPhoto();
   if(!['image/jpeg','image/png','image/webp'].includes(file.type) || file.size>8*1024*1024){
    showStatus('Choose a JPG, PNG or WebP under 8 MB.');dispatch('cc:photo-upload-start');dispatch('cc:photo-upload-finish');return;
   }
   currentFile=file;remove.hidden=false;objectUrl=URL.createObjectURL(file);
   const picture=q('[data-main-image]');picture.src=objectUrl;picture.hidden=false;
   q('[data-upload-empty]').hidden=true;q('[data-zoom]').hidden=false;q('[data-replace-photo]').hidden=false;
   analyse(file);
  });
 }
 const all=()=>document.querySelectorAll('[data-cc-builder]').forEach(init);
 if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',all);else all();
 document.addEventListener('shopify:section:load',all);
})();
